/**
 * Mode dégradé (CP-12) — la feuille de coupure, vue du moteur.
 *
 * Pendant une coupure de courant, rien ne s'écrit dans la plateforme : on
 * note sur papier. Le retour du courant demandait jusqu'ici de tout retaper,
 * ligne à ligne, dans « Noter l'entrée ». Une feuille de vingt-deux lignes
 * coûtait vingt minutes à l'accueil, au pire moment — celui où la file
 * reprend.
 *
 * D'où cette feuille : c'est NOUS qui l'imprimons, donc nous la dessinons
 * pour qu'une machine sache la relire. Quatre partis pris, chacun répondant
 * à une faiblesse mesurée de la lecture automatique :
 *
 * 1. **Un marqueur imprimé** (`AFS-COUPURE-V1`) en clair sur la feuille.
 *    Retrouvé dans le texte lu, il dit que c'est bien notre gabarit, et
 *    lequel — donc l'ordre des colonnes, même si la ligne de titres se lit
 *    mal.
 * 2. **Des numéros de ligne pré-imprimés.** L'OCR lit parfaitement des
 *    chiffres imprimés : ils ancrent les lignes quand l'écriture à la main
 *    résiste, et disent tout de suite quelle ligne manque.
 * 3. **Le papier ne porte que ce que la plateforme ignore** : l'heure, le
 *    matricule, de quoi reconnaître la personne, la prestation. Le nom
 *    officiel, la société, l'offre, le type et le verdict d'éligibilité,
 *    elle les sait déjà — les faire écrire à la main serait recopier pour
 *    rien, et ouvrir autant d'occasions de fausse donnée.
 * 4. **Rien ne s'écrit sans un œil humain.** Ce module rend une grille à
 *    vérifier, jamais des visites. C'est la règle de l'import DNS : la
 *    lecture pré-remplit, elle ne décide pas.
 *
 * Pur : ni fichier, ni base.
 */
import { normaliserLibelle } from '../dns/synonymes.js';
/**
 * Imprimé en clair sur chaque feuille. Normalisé avant comparaison : l'OCR
 * rend les tirets de façon capricieuse, et la casse aussi.
 */
export const MARQUEUR_FEUILLE = 'AFS-COUPURE-V1';
/** Les colonnes de la feuille, dans l'ordre imprimé. */
export const CHAMPS_FEUILLE = ['heure', 'matricule', 'nom', 'prestation', 'observation'];
/**
 * Les titres imprimés, et les formes que l'OCR en rend.
 *
 * Source unique : l'écran d'impression prend le premier libellé de chaque
 * champ, la lecture reconnaît les autres. Un titre changé à l'impression
 * sans l'être ici casserait la relecture en silence.
 */
export const ENTETES_FEUILLE = {
    numero: ['N°', 'N', 'NO', 'NUM'],
    heure: ['HEURE', 'HEURES', 'HRE'],
    matricule: ['MATRICULE', 'MATRICULES', 'MLE', 'MATR'],
    nom: ['NOM ET PRENOMS', 'NOM ET PRENOM', 'NOM PRENOMS', 'NOM', 'NOMS'],
    prestation: ['PRESTATION', 'PRESTATIONS', 'PREST', 'MOTIF'],
    observation: ['OBSERVATION', 'OBSERVATIONS', 'OBS', 'REMARQUE'],
};
/**
 * Les codes de prestation, deux lettres, imprimés en pied de feuille.
 *
 * Choisis sur MESURE (07/10/2026), pas au goût : 87 lettres manuscrites du
 * TL, découpées sur une vraie photo de feuille, recomposées en codes de deux
 * lettres et relues par le service de lecture. Les lettres sûres : K 96 %,
 * F 92 %, R 90 %, B 86 %, H 85 %, A 83 %, D 81 %, L 78 %, I 76 % ; la pire :
 * G, 14 % — d'où `RL` et non `GL` pour généraliste. Une lettre seule dans sa
 * case, elle, n'est souvent même pas détectée : deux lettres le sont mieux.
 *
 * Deux codes ne partagent jamais leur première lettre, ni leur seconde : une
 * lettre mal lue ne tombe donc jamais sur un autre code valide. Ces neuf
 * codes couvrent 96 % des passages du registre de septembre ; les autres
 * prestations s'écrivent en clair dans l'observation, et se choisissent à
 * l'écran.
 */
export const CODES_PRESTATION = {
    RL: 'GÉNÉRALISTE',
    DS: 'DENTISTE',
    HO: 'OPHTALMOLOGIE',
    AN: 'ANALYSES',
    LI: 'GYNÉCOLOGIE',
    ST: 'VISITE SYSTÉMATIQUE',
    BH: 'VISITE D’EMBAUCHE',
    ER: 'ÉCHOGRAPHIE',
    ND: 'ENDOCRINOLOGIE',
};
/**
 * Les codes d'une lettre de la première feuille, imprimée jusqu'au
 * 07/10/2026 : des exemplaires remplis circulent encore. Lus, jamais
 * réimprimés.
 */
const CODES_D_UNE_LETTRE = {
    G: 'GÉNÉRALISTE',
    D: 'DENTISTE',
    O: 'OPHTALMOLOGIE',
    A: 'ANALYSES',
    Y: 'GYNÉCOLOGIE',
    E: 'ÉCHOGRAPHIE',
    V: 'VISITE SYSTÉMATIQUE',
    B: 'VISITE D’EMBAUCHE',
    T: 'MÉDECINE DU TRAVAIL',
    C: 'CPN',
    K: 'KINÉSITHÉRAPIE',
    S: 'SOINS',
    P: 'PHARMACIE',
};
/**
 * Un code ne contient aucun chiffre : un chiffre lu est une lettre mal lue.
 * Les confusions mesurées le 07/10 — H lu 4, O lu 0, I lu 1, R lu 2.
 */
const LETTRE_DU_CHIFFRE = {
    '0': 'O',
    '1': 'I',
    '2': 'R',
    '4': 'H',
    '5': 'S',
    '8': 'B',
};
export function ligneFeuilleVide(numeroLigne) {
    return {
        numeroLigne,
        heure: '',
        matricule: '',
        nom: '',
        prestation: '',
        observation: '',
        aVerifier: [],
    };
}
/**
 * Une ligne de bruit : ni heure, ni matricule, ni prestation, et un « nom »
 * de moins de trois lettres — un trait, une tache, un bord de case lu comme
 * « k » ou « & ». Constaté le 07/10 sur une photo prise de loin : six lignes
 * de débris à écarter une à une. Elle n'est pas proposée.
 */
export function ligneDeBruit(ligne) {
    return (ligne.heure === '' &&
        ligne.matricule === '' &&
        ligne.prestation === '' &&
        ligne.nom.replace(/[^\p{L}]/gu, '').length < 3);
}
export function ligneFeuilleEstVide(ligne) {
    return (ligne.heure === '' &&
        ligne.matricule === '' &&
        ligne.nom === '' &&
        ligne.prestation === '' &&
        ligne.observation === '');
}
/**
 * Les confusions de l'OCR sur des chiffres écrits à la main, et elles
 * seules : `O` pour zéro, `I` et `L` pour un, `S` pour cinq, `B` pour huit.
 *
 * Elles ne s'appliquent QUE dans un champ qui ne contient que des chiffres —
 * une heure. Les appliquer à un matricule changerait `S1` en `51` et
 * désignerait quelqu'un d'autre. Et toute cellule qui a eu besoin d'une
 * substitution repart en « à vérifier » : on corrige pour proposer, jamais
 * pour affirmer.
 */
const CHIFFRES_CONFONDUS = {
    O: '0',
    Q: '0',
    I: '1',
    L: '1',
    '|': '1',
    S: '5',
    B: '8',
    G: '6',
    Z: '2',
};
/** Bornes d'ouverture d'un centre. Une heure en dehors se lit, mais se signale. */
const HEURE_MIN = 5;
const HEURE_MAX = 21;
/**
 * L'heure notée à la main, dans les graphies qu'on trouve sur les registres
 * d'AFS : `8h15`, `8 h 15`, `08:15`, `08.15`, `0815`, `8h`.
 *
 * Ne devine pas une minute absente : `8h` devient `08:00`, ce que l'agent a
 * voulu dire. La ligne reste de toute façon à vérifier dès que la lecture a
 * demandé le moindre effort.
 */
export function lireHeure(texte) {
    const brut = texte.trim();
    if (brut === '')
        return { valeur: '', incertaine: false };
    // `H` est le séparateur français : il se neutralise AVANT les
    // substitutions, sans quoi `8H15` deviendrait `8415`.
    const sansSeparateurH = brut.toUpperCase().replace(/H/gu, ':');
    let substitue = false;
    let lu = '';
    for (const caractere of sansSeparateurH) {
        const remplace = CHIFFRES_CONFONDUS[caractere];
        if (remplace === undefined) {
            lu += caractere;
            continue;
        }
        substitue = true;
        lu += remplace;
    }
    const chiffres = lu.replace(/\s+/gu, '');
    const separe = /^(\d{1,2})[:.,\-/](\d{0,2})$/u.exec(chiffres);
    const colle = /^(\d{3,4})$/u.exec(chiffres.replace(/[:.]/gu, ''));
    let heures;
    let minutes;
    let sansSeparateur = false;
    // « 10:5 » : 10:05, ou un chiffre perdu à la lecture — sur une vraie photo,
    // 10:57 a été lu ainsi (07/10/2026). Proposé, jamais affirmé.
    let minuteTronquee = false;
    if (separe !== null) {
        heures = Number(separe[1]);
        minutes = separe[2] === '' ? 0 : Number(separe[2]);
        minuteTronquee = separe[2]?.length === 1;
    }
    else if (colle !== null) {
        const n = colle[1] ?? '';
        heures = Number(n.slice(0, n.length - 2));
        minutes = Number(n.slice(-2));
        sansSeparateur = true;
    }
    else {
        return { valeur: '', incertaine: false };
    }
    if (heures > 23 || minutes > 59)
        return { valeur: '', incertaine: false };
    return {
        valeur: `${String(heures).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
        incertaine: substitue || sansSeparateur || minuteTronquee || heures < HEURE_MIN || heures > HEURE_MAX,
    };
}
/**
 * Un matricule lu : majuscules, espaces et ponctuation de séparation
 * retirés. **Aucune substitution de caractère** — c'est la clé qui désigne
 * une personne, et un `S` changé en `5` désigne quelqu'un d'autre. Un
 * matricule se confirme en retrouvant la personne, pas en le retouchant.
 */
export function lireMatricule(texte) {
    return texte
        .toUpperCase()
        .replace(/[^A-Z0-9-]/gu, '')
        .replace(/^-+|-+$/gu, '');
}
/** Combien de positions diffèrent entre deux codes de même longueur. */
export function lettresDifferentes(a, b) {
    let n = 0;
    for (let i = 0; i < a.length; i++)
        if (a.charAt(i) !== b.charAt(i))
            n += 1;
    return n;
}
/**
 * La prestation d'un code lu. Exact : retenue. À une lettre d'un seul code :
 * proposée, mais signalée — c'est l'agent qui confirme. Sinon vide : une
 * prestation devinée s'inscrirait au registre sans que personne la voie.
 * Un libellé écrit en toutes lettres est gardé tel quel.
 */
export function lirePrestation(texte) {
    const brut = texte.trim();
    if (brut === '')
        return { valeur: '', incertaine: false };
    const compact = brut.toUpperCase().replace(/[^A-Z0-9]/gu, '');
    if (/^[A-Z]$/u.test(compact)) {
        return { valeur: CODES_D_UNE_LETTRE[compact] ?? '', incertaine: false };
    }
    if (compact.length === 2) {
        const code = compact.replace(/[0-9]/gu, (c) => LETTRE_DU_CHIFFRE[c] ?? c);
        const exact = CODES_PRESTATION[code];
        if (exact !== undefined)
            return { valeur: exact, incertaine: code !== compact };
        const proches = Object.keys(CODES_PRESTATION).filter((c) => lettresDifferentes(c, code) === 1);
        const [seul] = proches;
        return proches.length === 1 && seul !== undefined
            ? { valeur: CODES_PRESTATION[seul] ?? '', incertaine: true }
            : { valeur: '', incertaine: false };
    }
    const normalise = normaliserLibelle(brut);
    const libelle = [...Object.values(CODES_PRESTATION), ...Object.values(CODES_D_UNE_LETTRE)].find((p) => normaliserLibelle(p) === normalise);
    return { valeur: libelle ?? brut, incertaine: false };
}
/** Le numéro pré-imprimé d'une ligne, quand la cellule ne porte que lui. */
function lireNumero(texte) {
    const n = /^(\d{1,3})[.)]?$/u.exec(texte.trim());
    return n === null ? null : Number(n[1]);
}
/**
 * Le tableau s'arrête là où la colonne des numéros cesse d'être une colonne
 * de numéros.
 *
 * Sous le tableau, la feuille porte ses mentions — la légende des
 * prestations, le rappel de geste, la ligne de signature. Lues telles
 * quelles, elles deviendraient des lignes de registre à écarter une par une.
 * Or nos numéros sont IMPRIMÉS : une ligne dont la première case porte du
 * texte qui n'est pas un nombre n'est plus une ligne du tableau.
 *
 * Une case vide, elle, n'arrête rien : c'est le cas d'une ligne dont l'OCR a
 * manqué le numéro, et perdre une entrée réelle serait bien pire que garder
 * une ligne de mention.
 */
function estFinDuTableau(cellule) {
    const texte = cellule.trim();
    return texte !== '' && lireNumero(texte) === null;
}
const sansAccents = (texte) => texte.normalize('NFD').replace(/\p{M}/gu, '').toUpperCase();
/** « RL GENERALISTE », et « G GENERALISTE » des feuilles déjà imprimées. */
const COUPLES_DE_LA_LEGENDE = [
    ...Object.entries(CODES_PRESTATION),
    ...Object.entries(CODES_D_UNE_LETTRE),
].map(([code, libelle]) => new RegExp(String.raw `${code}\s*${sansAccents(libelle).split(/[\s’']/u)[0] ?? ''}`, 'u'));
/**
 * Le pied de la feuille, reconnu à ce qu'il dit : la légende des
 * prestations, ou la consigne d'écriture. La règle des numéros ne suffit
 * pas quand la lecture rate le début de la mention : la case « N° » reste
 * vide, et la légende devenait une ligne de registre à vérifier (photo
 * réelle, 07/10/2026). Deux couples « lettre libellé » au moins : un nom
 * n'en porte jamais deux.
 */
function estMentionDePied(ligne) {
    const texte = sansAccents(ligne.join(' '));
    if (/CAPITALES D.?IMPRIMERIE|EN UNE LETTRE/u.test(texte))
        return true;
    return COUPLES_DE_LA_LEGENDE.filter((motif) => motif.test(texte)).length >= 2;
}
/** Le marqueur imprimé a-t-il été retrouvé dans ce que la lecture a rendu ? */
export function porteLeMarqueur(tableau) {
    const attendu = normaliserLibelle(MARQUEUR_FEUILLE);
    return tableau
        .slice(0, 8)
        .some((ligne) => ligne.some((cellule) => normaliserLibelle(cellule).includes(attendu)));
}
/** Au-delà, ce ne sont plus des titres : une feuille porte son en-tête tout en haut. */
const LIGNES_EXAMINEES = 12;
/** En dessous, une ligne de données passerait pour un en-tête. */
const TITRES_MIN = 2;
/** Les colonnes étroites, aux titres d'un seul mot : celles qui fusionnent. */
const CHAMPS_COURTS = ['numero', 'heure', 'matricule'];
/** Les champs que désigne un titre fait de plusieurs titres courts (« HEURE MATRICULE »). */
function champsDuTitreFusionne(cellule) {
    const mots = normaliserLibelle(cellule)
        .split(' ')
        .filter((m) => m !== '');
    const champs = [];
    for (const mot of mots) {
        const champ = CHAMPS_COURTS.find((c) => ENTETES_FEUILLE[c].some((libelle) => normaliserLibelle(libelle) === mot));
        if (champ === undefined || champs.includes(champ))
            return [];
        champs.push(champ);
    }
    return champs;
}
/**
 * Répartit une cellule lue sous un titre fusionné : « 2 09:45 1029 » →
 * numéro 2, heure 09:45, matricule 1029. Par la forme de chaque morceau :
 * le numéro pré-imprimé est un petit nombre en tête, l'heure porte un
 * séparateur (« : », « h », « . »), le matricule est le reste.
 */
function repartirCelluleFusionnee(texte, champs) {
    const morceaux = texte.split(/\s+/u).filter((m) => m !== '');
    const resultat = {};
    if (champs.includes('numero') && /^\d{1,3}[.)]?$/u.test(morceaux[0] ?? '')) {
        resultat.numero = morceaux.shift() ?? '';
    }
    if (champs.includes('heure')) {
        const i = morceaux.findIndex((m) => /^[0-9OoIl|]{1,2}\s*[:hH.]/u.test(m));
        if (i >= 0)
            resultat.heure = morceaux.splice(i, 1)[0] ?? '';
        else if (!champs.includes('matricule'))
            resultat.heure = morceaux.splice(0).join(' ');
    }
    if (champs.includes('matricule'))
        resultat.matricule = morceaux.join(' ');
    return resultat;
}
/**
 * Où sont les colonnes. On garde la ligne qui reconnaît le plus de titres,
 * parmi les premières seulement — plus bas, ce sont des données, et un
 * patient dont le nom se lit « NOM » ferait passer sa ligne pour un en-tête.
 */
export function mappingFeuille(tableau) {
    let meilleur = null;
    let reconnus = 0;
    for (let i = 0; i < Math.min(tableau.length, LIGNES_EXAMINEES); i++) {
        const ligne = tableau[i] ?? [];
        const colonnes = {};
        for (const [champ, libelles] of Object.entries(ENTETES_FEUILLE)) {
            const index = ligne.findIndex((cellule) => {
                const lu = normaliserLibelle(cellule);
                return lu !== '' && libelles.some((libelle) => normaliserLibelle(libelle) === lu);
            });
            if (index >= 0)
                colonnes[champ] = index;
        }
        // « N° HEURE », « HEURE MATRICULE » d'un seul tenant : quand l'écriture
        // de deux colonnes étroites se touche presque, la reconstruction ne voit
        // plus leur frontière et les fusionne (deux photos réelles du 07/10). Le
        // titre fusionné les désigne toutes ; la cellule se répartit à la lecture.
        ligne.forEach((cellule, index) => {
            const champs = champsDuTitreFusionne(cellule);
            if (champs.length < 2)
                return;
            for (const champ of champs)
                colonnes[champ] ??= index;
        });
        const n = Object.keys(colonnes).length;
        if (n > reconnus) {
            reconnus = n;
            meilleur = { ligneEntete: i + 1, colonnes };
        }
    }
    if (meilleur === null || reconnus < TITRES_MIN)
        return null;
    // Sans la colonne du matricule ni celle du nom, on ne saurait pas de qui
    // la feuille parle : mieux vaut annoncer qu'on n'a pas su lire.
    const { matricule, nom } = meilleur.colonnes;
    return matricule === undefined && nom === undefined ? null : meilleur;
}
/**
 * Les lignes de la feuille, telles que la lecture les propose.
 *
 * Une cellule vide ne devient jamais une valeur, et une valeur douteuse
 * repart en « à vérifier » plutôt qu'en valeur sûre : sur un registre
 * d'affluences, une heure fausse déforme la courbe de la journée, et un
 * matricule faux inscrit le passage au dossier de quelqu'un d'autre.
 */
export function lignesFeuilleDepuisTableau(tableau, mapping) {
    const { colonnes } = mapping;
    // Les champs courts qui partagent une même colonne : leur titre a fusionné.
    const fusionnes = CHAMPS_COURTS.filter((champ) => colonnes[champ] !== undefined &&
        CHAMPS_COURTS.some((autre) => autre !== champ && colonnes[autre] === colonnes[champ]));
    const cellule = (ligne, champ) => {
        const index = colonnes[champ];
        const texte = index === undefined ? '' : (ligne[index] ?? '').trim();
        if (!fusionnes.includes(champ))
            return texte;
        const memeColonne = fusionnes.filter((c) => colonnes[c] === index);
        return repartirCelluleFusionnee(texte, memeColonne)[champ] ?? '';
    };
    const lignes = [];
    let rang = 0;
    for (const brute of tableau.slice(mapping.ligneEntete)) {
        rang += 1;
        if (colonnes.numero !== undefined && estFinDuTableau(cellule(brute, 'numero')))
            break;
        if (estMentionDePied(brute))
            break;
        const heureBrute = cellule(brute, 'heure');
        const heure = lireHeure(heureBrute);
        const matricule = lireMatricule(cellule(brute, 'matricule'));
        const aVerifier = [];
        // Une heure écrite mais illisible est pire qu'une heure absente : elle
        // se remarque à l'écran seulement si on la signale.
        if (heure.incertaine || (heure.valeur === '' && heureBrute !== ''))
            aVerifier.push('heure');
        // Le matricule vient d'une écriture à la main : il reste douteux tant
        // qu'une personne du référentiel ne l'a pas confirmé. C'est le serveur
        // qui lève ce doute, en retrouvant la personne — jamais l'OCR seul.
        if (matricule !== '')
            aVerifier.push('matricule');
        const prestation = lirePrestation(cellule(brute, 'prestation'));
        if (prestation.incertaine)
            aVerifier.push('prestation');
        const ligne = {
            numeroLigne: lireNumero(cellule(brute, 'numero')) ?? rang,
            heure: heure.valeur,
            matricule,
            nom: cellule(brute, 'nom').replace(/\s+/gu, ' '),
            prestation: prestation.valeur,
            observation: cellule(brute, 'observation'),
            aVerifier,
        };
        if (!ligneFeuilleEstVide(ligne) && !ligneDeBruit(ligne))
            lignes.push(ligne);
    }
    return lignes;
}
//# sourceMappingURL=feuille.js.map